import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { GapSizeType } from '../../../theme/type/gap-type';
import { cn } from '../../../utils/tailwind';

type SwitcherOption<T extends string> = {
    value: T;
    label: React.ReactNode;
};

const marginBottomClass: Record<GapSizeType, string> = {
    none: 'mb-0',
    xxsmall: 'mb-xxsmall',
    xsmall: 'mb-xsmall',
    vsmall: 'mb-vsmall',
    small: 'mb-small',
    medium: 'mb-medium',
    large: 'mb-large',
    vlarge: 'mb-vlarge',
    xlarge: 'mb-xlarge',
    xxlarge: 'mb-xxlarge',
};

type SwitcherProps<T extends string> = {
    value: T;
    onChange: (value: T) => void;
    firstOption?: SwitcherOption<T>;
    secondOption?: SwitcherOption<T>;
    options?: SwitcherOption<T>[];
    size?: 'small' | 'default' | 'large';
    fullWidth?: boolean;
    activeColor?: 'auto' | 'primary' | 'positive';
    activeBackgroundColor?: string;
    marginBottom?: GapSizeType;
    disabled?: boolean;
};

export const Switcher = <T extends string>({
    onChange,
    value,
    firstOption,
    secondOption,
    options,
    size = 'default',
    fullWidth = false,
    activeColor = 'auto',
    activeBackgroundColor,
    marginBottom = 'none',
    disabled = false,
}: SwitcherProps<T>): React.ReactElement | null => {
    const theme = useTheme();
    const [activeWidth, setActiveWidth] = useState(0);
    const [activeLeft, setActiveLeft] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);
    const switchInnerRef = useRef<HTMLDivElement | null>(null);
    const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

    const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
    const resolvedOptions =
        options ?? [firstOption, secondOption].filter((option): option is SwitcherOption<T> => Boolean(option));

    const recalculatingBackgroundPosition = (newValue: T) => {
        const activeIndex = resolvedOptions.findIndex((option) => option.value === newValue);
        const activeOption = activeIndex >= 0 ? optionRefs.current[activeIndex] : null;

        if (activeOption) {
            setActiveWidth(activeOption.offsetWidth);
            setActiveLeft(activeOption.offsetLeft);
            setIsInitialized(true);
        }
    };

    useIsomorphicLayoutEffect(() => {
        recalculatingBackgroundPosition(value);
    }, [value, resolvedOptions.length]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const recalculate = () => {
            recalculatingBackgroundPosition(value);
        };

        const animationFrame = window.requestAnimationFrame(recalculate);
        window.addEventListener('resize', recalculate);

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(recalculate);

            if (switchInnerRef.current) {
                resizeObserver.observe(switchInnerRef.current);
            }

            optionRefs.current.forEach((option) => {
                if (option) {
                    resizeObserver?.observe(option);
                }
            });
        }

        let isMounted = true;
        document.fonts?.ready.then(() => {
            if (isMounted) {
                recalculate();
            }
        });

        return () => {
            isMounted = false;
            window.cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', recalculate);
            resizeObserver?.disconnect();
        };
    }, [value, resolvedOptions.length]);

    if (resolvedOptions.length < 2) {
        return null;
    }

    const handleChange = (newValue: T) => {
        if (disabled) {
            return;
        }

        onChange(newValue);
        recalculatingBackgroundPosition(newValue);
    };

    let resolvedActiveBackgroundColor = activeBackgroundColor ?? theme.palette.color.positive.main;

    if (!activeBackgroundColor) {
        if (activeColor === 'primary') {
            resolvedActiveBackgroundColor = theme.palette.color.primary.main;
        } else if (activeColor === 'positive') {
            resolvedActiveBackgroundColor = theme.palette.color.positive.main;
        } else if (value === resolvedOptions[0]?.value) {
            resolvedActiveBackgroundColor = theme.palette.color.primary.main;
        }
    }

    const containerSizeClass = cn(
        size === 'large' && 'min-h-[4.8rem] p-[0.4rem]',
        size === 'default' && 'min-h-[3.8rem] p-[0.3rem]',
        size === 'small' && 'min-h-[2.7rem] p-[0.2rem]',
    );

    const optionSizeClass = cn(
        size === 'large' && 'px-[1.8rem] py-[1.1rem] text-[1.5rem]',
        size === 'default' && 'px-[1rem] py-[0.5rem] text-[1.4rem]',
        size === 'small' && 'px-[0.8rem] py-[0.1rem] text-[1.1rem]',
    );

    return (
        <div
            className={cn(
                'flex overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                fullWidth && 'w-full',
                marginBottomClass[marginBottom],
            )}
        >
            <div
                className={cn(
                    'inline-flex shrink-0 rounded-[5rem] border border-solid border-border bg-box',
                    containerSizeClass,
                    fullWidth && 'min-w-full w-max',
                )}
            >
                <div ref={switchInnerRef} className="relative flex w-full rounded-[5rem]">
                    <div
                        className={cn(
                            'absolute h-full rounded-[5rem] will-change-[left,width]',
                            isInitialized &&
                                'transition-[left,width,background] duration-500 ease-[cubic-bezier(0.3,-0.3,0.3,1.3)]',
                        )}
                        style={{
                            width: `${activeWidth}px`,
                            left: `${activeLeft}px`,
                            background: resolvedActiveBackgroundColor,
                        }}
                    />
                    {resolvedOptions.map((option, index) => (
                        <div
                            key={option.value}
                            ref={(element) => {
                                optionRefs.current[index] = element;
                            }}
                            onClick={disabled ? undefined : () => handleChange(option.value)}
                            className={cn(
                                'relative flex flex-1 items-center justify-center whitespace-nowrap text-center font-bold transition-colors duration-500 ease-in-out',
                                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                                optionSizeClass,
                                value === option.value ? 'text-neutral-white' : 'text-neutral-gray',
                            )}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
